import type { CategoryKind, ShareInput, TransactionInput } from '@fin/shared';
import { NetworkError, patch, post, put } from '../../lib/api.ts';
import { enqueue } from '../../lib/offline.ts';
import type { Mode } from './model.tsx';
export interface TransactionSave {
    id?: number;
    mode: Mode;
    kind: CategoryKind;
    clientId: string;
    walletId: number;
    categoryId: number;
    toWalletId?: number;
    amount: number;
    amountTo?: number;
    fee?: number;
    date: string;
    note: string;
    contactId: number | null;
    debtId: number | null;
    periodItemId: number | null;
    excludeFromReport: boolean;
    /** undefined: the server decides (a transfer: by its wallets) */
    inPeriod?: boolean;
    /** an income adds to the period's free money */
    periodIncome?: boolean;
    /** other people's parts, only for a new expense or transfer */
    shares?: ShareInput[];
    newContact: string;
    currency: string;
    destinationCurrency?: string;
    debtTitle: string;
    label: string;
}
/** Save orchestration is independent of the form view. Only legacy single creates queue offline. */
export async function saveTransaction(input: TransactionSave): Promise<{
    queued: boolean;
    contactChanged: boolean;
}> {
    let queued = false, contactChanged = false;
    const create = async (path: '/transactions' | '/transfers', body: Record<string, unknown>) => {
        try {
            await post(path, body);
        }
        catch (error) {
            if (!(error instanceof NetworkError))
                throw error;
            enqueue({ clientId: input.clientId, path, body, label: input.label });
            queued = true;
        }
    };
    if (input.mode === 'transfer') {
        if (!input.toWalletId)
            throw new Error('Выберите кошелёк получателя');
        if (input.id) {
            // both legs at once; the fee is its own expense
            await put(`/transfers/${input.id}`, { fromWalletId: input.walletId, toWalletId: input.toWalletId, amount: input.amount, amountTo: input.destinationCurrency !== input.currency ? input.amountTo : undefined, date: input.date, note: input.note, periodItemId: input.periodItemId, inPeriod: input.inPeriod, periodIncome: input.periodIncome });
            return { queued, contactChanged };
        }
        await create('/transfers', { fromWalletId: input.walletId, toWalletId: input.toWalletId, amount: input.amount, amountTo: input.destinationCurrency !== input.currency ? input.amountTo : undefined, fee: input.fee || undefined, date: input.date, note: input.note, periodItemId: input.periodItemId ?? undefined, inPeriod: input.inPeriod, periodIncome: input.periodIncome, shares: input.shares?.length ? input.shares : undefined, clientId: input.clientId });
    }
    else {
        const opensDebt = !input.id && (input.kind === 'debt' || input.kind === 'loan') && !input.debtId;
        let contactId = input.contactId;
        if (!opensDebt && input.mode === 'debt' && input.newContact.trim()) {
            contactId = (await post<{
                id: number;
            }>('/contacts', { name: input.newContact.trim() })).id;
            contactChanged = true;
        }
        const body: TransactionInput = { walletId: input.walletId, categoryId: input.categoryId, amount: input.amount, date: input.date, note: input.note, contactId, debtId: input.debtId, periodItemId: input.periodItemId, excludeFromReport: input.excludeFromReport, inPeriod: input.inPeriod, periodIncome: input.periodIncome };
        if (input.id)
            await patch(`/transactions/${input.id}`, body);
        else if (opensDebt) {
            await post('/debts/open', { transaction: { ...body, clientId: input.clientId }, debt: { direction: input.kind === 'debt' ? 'borrowed' : 'lent', title: input.debtTitle, contactId, contactName: input.newContact.trim() || undefined, currency: input.currency, principal: input.amount, startDate: input.date } });
            contactChanged = true;
        }
        else
            await create('/transactions', { ...body, shares: input.shares?.length ? input.shares : undefined, clientId: input.clientId });
    }
    return { queued, contactChanged };
}
